import React from 'react';
import { createPortal } from 'react-dom';
import { Box, Typography } from '@mui/material';
import { 
  getEnvironmentConfig, 
  shouldShowEnvironmentBanner,
  currentEnvironment 
} from '../../config/environment';
import { isNativeMobile } from '../../config/api';
import { Z_INDEX } from '../../constants/zIndex';
import { fontSizeFixed } from '../../util/responsiveUtils';

/**
 * Environment Banner Component
 * 
 * Displays a clear visual indicator when the app is running against staging or development environments.
 * This is a safety feature to prevent confusion about which backend the app is connected to.
 * 
 * - Shows on STAGING: Orange banner with "STAGING" label
 * - Shows on DEVELOPMENT: Green banner with "DEV" label  
 * - Hidden on PRODUCTION: No banner shown for clean production UX
 */
export const EnvironmentBanner: React.FC = () => {
  // Only show banner in non-production environments
  if (!shouldShowEnvironmentBanner()) {
    return null;
  }

  const config = getEnvironmentConfig();
  
  // Only show on mobile apps (web has other ways to identify environment)
  if (!isNativeMobile()) {
    return null;
  }

  const banner = (
    <Box
      sx={{
        position: 'fixed',
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 4px)',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: Z_INDEX.ENVIRONMENT_BANNER,
        backgroundColor: config.bannerColor,
        color: config.bannerTextColor,
        py: 0.25,
        px: 1,
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        opacity: 0.85,
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
      }}
    >
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: fontSizeFixed.rem06,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          lineHeight: 1,
        }}
      >
        {currentEnvironment === 'staging' ? 'STAGING' : 'DEV'}
      </Typography>
    </Box>
  );

  // Portal to document.body so z-index stacks above navigation buttons (which are also portaled to body)
  if (typeof document !== 'undefined' && document.body) {
    return createPortal(banner, document.body);
  }
  return banner;
};

/**
 * Environment Info Component
 * 
 * A smaller, inline component that can be placed in settings or debug screens
 * to show current environment information.
 */
export const EnvironmentInfo: React.FC = () => {
  const config = getEnvironmentConfig();
  
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        backgroundColor: `${config.bannerColor}15`,
        border: `1px solid ${config.bannerColor}`,
      }}
    >
      <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
        Environment: {config.displayName}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', opacity: 0.8 }}>
        API: {config.apiUrl}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', opacity: 0.8 }}>
        WebSocket: {config.wsScheme}://{config.wsHost}:{config.wsPort}
      </Typography>
    </Box>
  );
};

export default EnvironmentBanner;

